import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDiscounts,
  fetchDiscount,
  createDiscount,
  updateDiscount,
  archiveDiscount,
  restoreDiscount,
  deleteDiscount,
  fetchDiscountAssignments,
  assignDiscountToStudents,
  assignDiscountToFiliere,
  assignDiscountToCampus,
  removeDiscountAssignment,
} from '@/services/tuitionService';
import type {
  DiscountQuery,
  CreateDiscountData,
  UpdateDiscountData,
  DiscountAssignmentsQuery,
  AssignDiscountToStudentsData,
  AssignDiscountToFiliereData,
  AssignDiscountToCampusData,
} from '@/types/tuition.types';

// Query keys
export const discountKeys = {
  all: ['discounts'] as const,
  lists: () => [...discountKeys.all, 'list'] as const,
  list: (query: DiscountQuery) => [...discountKeys.lists(), query] as const,
  details: () => [...discountKeys.all, 'detail'] as const,
  detail: (id: string) => [...discountKeys.details(), id] as const,
  assignments: (discountId: string) => [...discountKeys.all, 'assignments', discountId] as const,
  assignmentList: (discountId: string, query: DiscountAssignmentsQuery) =>
    [...discountKeys.assignments(discountId), query] as const,
};

// ==================== Discount Config Hooks ====================

/**
 * Hook to fetch discounts with pagination and filtering
 */
export function useDiscounts(query: DiscountQuery = {}) {
  return useQuery({
    queryKey: discountKeys.list(query),
    queryFn: () => fetchDiscounts(query),
    retry: 1,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single discount by ID
 */
export function useDiscount(id: string) {
  return useQuery({
    queryKey: discountKeys.detail(id),
    queryFn: () => fetchDiscount(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new discount
 */
export function useCreateDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDiscountData) => createDiscount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to update a discount
 */
export function useUpdateDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDiscountData }) =>
      updateDiscount(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: discountKeys.detail(id) });
    },
  });
}

/**
 * Hook to archive a discount
 */
export function useArchiveDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => archiveDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to restore an archived discount
 */
export function useRestoreDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => restoreDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to permanently delete a discount
 */
export function useDeleteDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

// ==================== Discount Assignment Hooks ====================

/**
 * Hook to fetch assignments for a discount
 */
export function useDiscountAssignments(
  discountId: string,
  query: DiscountAssignmentsQuery = {}
) {
  return useQuery({
    queryKey: discountKeys.assignmentList(discountId, query),
    queryFn: () => fetchDiscountAssignments(discountId, query),
    enabled: !!discountId,
  });
}

/**
 * Hook to assign a discount to specific students
 */
export function useAssignDiscountToStudents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignDiscountToStudentsData) => assignDiscountToStudents(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: discountKeys.assignments(data.discountConfigId),
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to assign a discount to a filière
 */
export function useAssignDiscountToFiliere() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignDiscountToFiliereData) => assignDiscountToFiliere(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: discountKeys.assignments(data.discountConfigId),
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to assign a discount to a campus
 */
export function useAssignDiscountToCampus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignDiscountToCampusData) => assignDiscountToCampus(data),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: discountKeys.assignments(data.discountConfigId),
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to remove a discount assignment
 */
export function useRemoveDiscountAssignment(discountId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) => removeDiscountAssignment(assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: discountKeys.assignments(discountId),
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

// ==================== Pending Discounts Hooks ====================

import {
  fetchPendingDiscounts,
  approveDiscount,
  rejectDiscount,
} from '@/services/tuitionService';

export const pendingDiscountKeys = {
  all: ['pendingDiscounts'] as const,
  list: (query?: { page?: number; limit?: number }) => [...pendingDiscountKeys.all, query] as const,
};

/**
 * Hook to fetch pending discounts awaiting approval
 */
export function usePendingDiscounts(query: { page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: pendingDiscountKeys.list(query),
    queryFn: () => fetchPendingDiscounts(query),
    // Always fetch fresh: a discount assigned elsewhere (e.g. by a Responsable
    // Marketing) must appear immediately when the DG/RF opens the tab from the
    // dashboard, instead of a stale cached "0".
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to approve a pending discount
 */
export function useApproveDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (discountId: string) => approveDiscount(discountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingDiscountKeys.all });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}

/**
 * Hook to reject a pending discount
 */
export function useRejectDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ discountId, reason }: { discountId: string; reason?: string }) =>
      rejectDiscount(discountId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingDiscountKeys.all });
      queryClient.invalidateQueries({ queryKey: discountKeys.lists() });
    },
  });
}
